import { format } from 'date-fns';
import { Button, Column, TableCell, TableRow, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { getMembershipStatusLabel, type BidHistoryItem } from '@tempmee/awards';
import { parseDateString } from '~/utils/DateUtils';
import { formatBidCalendarDate } from '../../shared';

interface IBidHistoryRowProps {
  item: BidHistoryItem;
  /** Called with the row's `cycleId` when "Apply" is clicked. */
  onApply: (cycleId: string) => void;
}

/**
 * Per Figma, the Cycle column shows the year only (e.g. "2026"), not the
 * full "Best in Dental - 2026" name. Prefer the server-derived `cycleYear`
 * (awards-service#35, now returned by GET /cycles). It stays optional in the
 * `@tempmee/awards` contract, so fall back to the application-open year when
 * absent — the award year — never parse it out of `cycleName` (a display
 * string the server may reword at any time).
 */
const displayCycleYear = (item: BidHistoryItem): string => {
  if (item.cycleYear != null) return String(item.cycleYear);
  return format(parseDateString(item.applicationOpenDate), 'yyyy');
};

/** One row of the BID history table (presentational). */
export const BidHistoryRow = ({ item, onApply }: IBidHistoryRowProps) => {
  const isApplicationDeadlinePast = item.membershipStatus === 'nominee' && !item.applyEligible;

  return (
    <TableRow data-cy={`bid-history-row-${item.cycleId}`}>
      <TableCell>
        <Typography>{displayCycleYear(item)}</Typography>
      </TableCell>
      <TableCell>
        <Column gap={1}>
          <Typography>{getMembershipStatusLabel(item.membershipStatus, 'desktop')}</Typography>
          {item.applyEligible && (
            <Typography variant="caption" color={colors.text.secondary}>
              Application Close Date: {formatBidCalendarDate(item.applicationCloseDate)}
            </Typography>
          )}
        </Column>
      </TableCell>
      <TableCell align="right">
        {item.applyEligible && (
          <Button data-cy="bid-apply-button" onClick={() => onApply(item.cycleId)}>
            Apply
          </Button>
        )}
        {isApplicationDeadlinePast && <Typography>Application Deadline past</Typography>}
      </TableCell>
    </TableRow>
  );
};
